<template>
  <div class="workspace-shell" :class="{ 'is-docked': docked }">
    <div class="workspace-content" :inert="drawerOpen ? '' : null">
      <router-view v-slot="{ Component, route: pageRoute }">
        <keep-alive>
          <WorkspacePage v-if="Component && pageRoute.meta.workspaceCache !== false" :key="workspaceCacheKey(pageRoute)" :view="Component" :location="snapshotWorkspaceRoute(pageRoute)" />
        </keep-alive>
        <WorkspacePage v-if="Component && pageRoute.meta.workspaceCache === false" :key="pageRoute.fullPath" :view="Component" :location="snapshotWorkspaceRoute(pageRoute)" />
      </router-view>
    </div>

    <button v-if="!isScriptStudioRoute && !docked && !drawerOpen" ref="launcher" class="workspace-launcher" type="button" aria-label="打开工作台导航" title="打开工作台导航（Alt + `）" aria-controls="workspace-navigation" :aria-expanded="drawerOpen" @click="openDrawer">
      <el-icon><Menu /></el-icon><span>工作台</span><el-icon class="launcher-arrow"><ArrowRight /></el-icon>
    </button>
    <Transition name="workspace-shade">
      <div v-if="drawerOpen" class="workspace-backdrop" @click="closeDrawer" />
    </Transition>
    <Transition name="workspace-drawer">
      <aside v-if="docked || drawerOpen" id="workspace-navigation" ref="navigationPanel" class="workspace-navigation" :class="{ 'is-overlay': !docked }" :role="docked ? undefined : 'dialog'" :aria-modal="docked ? undefined : 'true'" aria-label="工作台导航" @keydown="trapFocus">
        <div class="workspace-brand">
          <span class="workspace-brand-mark"><el-icon><VideoCamera /></el-icon></span>
          <div><strong>于垣创作</strong><span>创作工作台</span></div>
        </div>
        <nav class="workspace-primary" aria-label="工作台主导航">
          <button v-for="entry in entries" :key="entry.id" type="button" class="workspace-nav-item" :class="{ active: activeEntry === entry.id }" :aria-current="activeEntry === entry.id ? 'page' : undefined" @click="openEntry(entry)">
            <el-icon><component :is="entry.icon" /></el-icon><span>{{ entry.label }}</span><i v-if="activeEntry === entry.id" />
          </button>
        </nav>
        <div v-if="projectId && !['VideoList', 'FeishuTables'].includes(route.name)" class="workspace-project-context">
          <div class="workspace-section-heading"><span>当前项目</span><button type="button" @click="openProjects">切换</button></div>
          <div class="workspace-project-name" :title="projectName"><el-icon><FolderOpened /></el-icon><strong>{{ projectName }}</strong></div>
        </div>
        <nav v-if="!['VideoList', 'FeishuTables'].includes(route.name)" class="workspace-tools" aria-label="创作工具">
          <div class="workspace-section-heading"><span>创作工具</span></div>
          <button v-for="tool in tools" :key="tool.id" type="button" class="workspace-nav-item" :class="{ active: activeTool === tool.id }" :aria-current="activeTool === tool.id ? 'page' : undefined" @click="openTool(tool.id)">
            <el-icon><component :is="tool.icon" /></el-icon><span>{{ tool.label }}</span><span v-if="activeTool === tool.id" class="workspace-current">当前</span>
          </button>
        </nav>
        <div class="workspace-footer">
          <p>让每个灵感，成为作品。</p>
          <div class="workspace-user"><span class="workspace-avatar">{{ userName.slice(0, 1) }}</span><span>{{ userName }}</span><button class="workspace-icon-button" type="button" aria-label="退出登录" title="退出登录" @click="signOut"><el-icon><SwitchButton /></el-icon></button></div>
          <span v-if="!docked" class="workspace-shortcut">快捷打开 <kbd>Alt</kbd> + <kbd>`</kbd></span>
        </div>
      </aside>
    </Transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getUserInfo, hasPermission, logout } from '@/api/auth'
import { getScripts } from '@/api/scripts'
import { SELECTED_SCRIPT_KEY, TOKEN_KEY } from '@/config/constants'
import WorkspacePage from './WorkspacePage.vue'
import { activeWorkspaceTool, snapshotWorkspaceRoute, workspaceCacheKey, workspaceEntries, workspaceNavigationKey, workspacePageTitle, workspaceToolLocation, workspaceTools } from './navigation'
import { openWorkspaceTab, registerWorkspaceTab } from './workspaceTabs'
import { selectWorkspaceScript, subscribeWorkspaceScript } from './scriptSelection'

const route = useRoute()
const router = useRouter()
const isScriptStudioRoute = computed(() => route.matched.some(record => ['/script-agent', '/quick-script'].includes(record.path)))
const unregisterTab = registerWorkspaceTab(router)
const drawerOpen = ref(false)
const launcher = ref(null)
const navigationPanel = ref(null)
const wideScreen = ref(window.matchMedia('(min-width: 901px)').matches)
const docked = computed(() => ['VideoList', 'FeishuTables'].includes(route.name) && wideScreen.value)
const entries = computed(() => { void route.fullPath; return workspaceEntries.filter(entry => hasPermission(entry.permission)) })
const tools = computed(() => { void route.fullPath; return workspaceTools.filter(tool => hasPermission(tool.permission)) })
const activeTool = computed(() => activeWorkspaceTool(route))
const activeEntry = computed(() => route.name === 'FeishuTables' ? 'tables' : route.name === 'InspirationMaterial' ? 'inspiration' : route.path.startsWith('/quick-script') ? 'quick-scripts' : route.path.startsWith('/script-agent') ? 'scripts' : 'projects')
const userName = computed(() => { void route.fullPath; const user = getUserInfo(); return user?.nickname || user?.username || '创作者' })
const projectId = ref('')
const projectNames = ref(new Map())
const projectName = computed(() => projectNames.value.get(projectId.value)?.title || projectNames.value.get(projectId.value)?.name || `剧本项目 ${projectId.value}`)
const previousLocations = new Map()
const sessionToken = localStorage.getItem(TOKEN_KEY)
let previousFocus = null
let disposed = false
try {
  const saved = JSON.parse(localStorage.getItem(SELECTED_SCRIPT_KEY) || 'null')
  if (saved?.id != null) { projectId.value = String(saved.id); projectNames.value.set(projectId.value, saved) }
} catch (_) { /* Ignore an outdated preference. */ }

function rememberProjects(items) {
  projectNames.value = new Map(items.map(item => [String(item.id), item]))
  if (projectId.value && !projectNames.value.has(projectId.value)) {
    projectId.value = ''
    previousLocations.clear()
  }
}
function applyProject(project) {
  if (project?.id == null) return
  const id = String(project.id)
  if (projectId.value !== id) previousLocations.clear()
  projectId.value = id
  projectNames.value.set(id, { ...projectNames.value.get(id), ...project })
}
function selectProject(project) { selectWorkspaceScript(project) }
const stopScriptSelection = subscribeWorkspaceScript(applyProject)
async function navigate(location) {
  try {
    const opened = await openWorkspaceTab(router, location)
    if (opened) closeDrawer(false)
    else ElMessage.warning('请允许本站打开新标签页，然后重试')
    return opened
  } catch (_) {
    ElMessage.error('页面加载失败，请重试')
    return false
  }
}
function openTool(id) {
  const tool = workspaceTools.find(item => item.id === id)
  if (!tool || !hasPermission(tool.permission)) return
  const routeScriptId = route.params.scriptId ?? route.query.scriptId
  const selectedScriptId = projectId.value || (!Array.isArray(routeScriptId) ? routeScriptId : '')
  let location = workspaceToolLocation(id, selectedScriptId, previousLocations.get(id))
  if (selectedScriptId && !['video', 'image'].includes(id)) {
    const target = router.resolve(location)
    location = { path: target.path, query: { ...target.query, scriptId: String(selectedScriptId) }, hash: target.hash }
  }
  return navigate(location)
}
function openEntry(entry) {
  if (hasPermission(entry.permission)) return navigate({ name: entry.name })
}
function openProjects() { return navigate({ name: 'VideoList' }) }
async function openDrawer() {
  if (isScriptStudioRoute.value || docked.value || drawerOpen.value) return
  // Keep modal editing sessions above navigation; cached teleports must not be
  // left visible over a different tool when using the keyboard shortcut.
  if ([...document.querySelectorAll('[aria-modal="true"], .creation-board, .yc-image-edit-modal')].some(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden')) return
  previousFocus = document.activeElement
  drawerOpen.value = true
  await nextTick()
  navigationPanel.value?.querySelector('button')?.focus()
}
function closeDrawer(restoreFocus = true) {
  drawerOpen.value = false
  if (restoreFocus) nextTick(() => (previousFocus?.isConnected ? previousFocus : launcher.value)?.focus())
}
function trapFocus(event) {
  if (docked.value || event.key !== 'Tab') return
  const focusable = [...navigationPanel.value.querySelectorAll('button:not(:disabled), [href], [tabindex="0"]')]
  const first = focusable[0], last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
}
function handleKeydown(event) {
  if (isScriptStudioRoute.value) return
  if (event.altKey && event.code === 'Backquote') {
    event.preventDefault()
    drawerOpen.value ? closeDrawer() : openDrawer()
  } else if (event.key === 'Escape' && drawerOpen.value) {
    event.preventDefault()
    event.stopImmediatePropagation()
    closeDrawer()
  }
}
function updateWidth(event) { wideScreen.value = event.matches; closeDrawer(false) }
function signOut() { logout(); router.replace({ name: 'Login' }) }
provide(workspaceNavigationKey, { openTool, openProjects, selectProject, rememberProjects, projectId, drawerOpen })

watch(() => route.fullPath, () => {
  const id = route.params.scriptId ?? (activeTool.value ? route.query.scriptId : null)
  if (id != null && !Array.isArray(id)) {
    if (projectId.value !== String(id)) previousLocations.clear()
    projectId.value = String(id)
  }
  if (activeTool.value) previousLocations.set(activeTool.value, route.fullPath)
  closeDrawer(false)
  document.title = workspacePageTitle(route)
}, { immediate: true, flush: 'sync' })

const media = window.matchMedia('(min-width: 901px)')
onMounted(async () => {
  media.addEventListener('change', updateWidth)
  window.addEventListener('keydown', handleKeydown, true)
  // Direct project links have no project-list visit from which to get the name.
  if (projectId.value && hasPermission('script:view')) {
    try {
      const response = await getScripts({ includeImageCover: true })
      const items = response?.data?.data ?? response?.data ?? response
      if (disposed || sessionToken !== localStorage.getItem(TOKEN_KEY)) return
      if (Array.isArray(items)) rememberProjects(items)
    } catch (_) { /* The page has its own retry UI. Navigation remains usable. */ }
  }
})
onBeforeUnmount(() => {
  disposed = true
  unregisterTab()
  stopScriptSelection()
  media.removeEventListener('change', updateWidth)
  window.removeEventListener('keydown', handleKeydown, true)
})
</script>

<style scoped>
.workspace-shell { --workspace-sidebar-width: 286px; min-height: 100vh; background: #000; color-scheme: dark; }
.workspace-content { min-width: 0; }
.is-docked > .workspace-content { padding-left: var(--workspace-sidebar-width); }
.workspace-navigation { position: fixed; inset: 0 auto 0 0; z-index: 4002; display: flex; flex-direction: column; width: var(--workspace-sidebar-width); max-width: calc(100vw - 32px); height: 100vh; height: 100dvh; overflow-y: auto; padding: 24px 0 24px 16px; background: #000; color: #eee; font-family: Inter, 'PingFang SC', 'Microsoft YaHei', sans-serif; scrollbar-width: none; color-scheme: dark; }
.workspace-navigation::-webkit-scrollbar { width: 0; height: 0; }
.workspace-navigation.is-overlay { box-shadow: 20px 0 70px #000b; }
.workspace-brand { display: flex; align-items: center; gap: 12px; padding: 0 20px 28px; }
.workspace-brand-mark { display: grid; place-items: center; width: 38px; height: 38px; flex-shrink: 0; border: 0; border-radius: 50%; color: #f2f2f2; background: #272727; font-size: 20px; }
.workspace-brand > div { flex: 1; }.workspace-brand strong { display: block; font-size: 18px; font-weight: 550; letter-spacing: 0; }.workspace-brand > div > span { display: block; margin-top: 5px; font-size: 12px; letter-spacing: 0; color: #888; }
.workspace-primary, .workspace-tools { display: flex; flex-direction: column; gap: 6px; padding-right: 16px; }
.workspace-nav-item { display: flex; align-items: center; gap: 18px; width: 100%; min-height: 52px; padding: 0 20px; border: 0; border-radius: 22px; background: transparent; color: #ededed; font: inherit; font-size: 16px; font-weight: 550; text-align: left; cursor: pointer; transition: background .16s, color .16s; }
.workspace-nav-item > .el-icon { width: 24px; flex: 0 0 24px; font-size: 24px; }.workspace-nav-item:hover { color: #fff; background: #202020; }.workspace-nav-item.active { color: #fff; background: #363636; }.workspace-nav-item > i:not(.el-icon) { margin-left: auto; width: 6px; height: 6px; border-radius: 50%; background: #5bd8b3; }
.workspace-project-context { margin: 16px 4px 6px 0; padding: 18px 0 0; border-top: 1px solid #202020; }.workspace-section-heading { display: flex; align-items: center; justify-content: space-between; padding: 0 20px 10px; font-size: 11px; letter-spacing: 0; color: #888; }.workspace-section-heading > button { border: 0; background: none; color: #aaa; font: inherit; cursor: pointer; }.workspace-project-name { display: flex; align-items: center; gap: 10px; padding: 0 20px 6px; color: #c5c5c5; }.workspace-project-name strong { overflow: hidden; font-size: 13px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }.workspace-project-name .el-icon { flex-shrink: 0; color: #999; }
.workspace-tools { margin-top: 18px; }.workspace-project-context + .workspace-tools { margin-top: 10px; }.workspace-tools .workspace-nav-item { min-height: 46px; font-size: 14px; }.workspace-tools .workspace-nav-item > .el-icon { width: 22px; flex-basis: 22px; font-size: 22px; }.workspace-current { margin-left: auto; font-size: 11px; font-weight: 400; color: #999; }
.workspace-footer { margin-top: auto; padding: 28px 16px 0 20px; }.workspace-footer > p { margin: 0 0 20px; color: #666; font-size: 11px; }.workspace-user { display: flex; align-items: center; gap: 10px; padding: 14px 0 0; border-top: 1px solid #202020; font-size: 13px; color: #aaa; }.workspace-user > span:nth-child(2) { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.workspace-avatar { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 50%; background: #272727; color: #eee; }.workspace-icon-button { display: grid; place-items: center; width: 32px; height: 32px; flex-shrink: 0; padding: 0; border: 0; border-radius: 50%; background: transparent; color: #999; cursor: pointer; }.workspace-icon-button:hover { background: #252525; color: #fff; }.workspace-shortcut { display: block; margin-top: 18px; text-align: center; color: #666; font-size: 11px; }.workspace-shortcut kbd { padding: 2px 5px; border: 1px solid #3a3a3a; border-radius: 5px; background: #1b1b1b; color: #aaa; font: inherit; }
.workspace-launcher { position: fixed; z-index: 1990; left: 0; top: 46%; display: flex; flex-direction: column; align-items: center; gap: 8px; width: 34px; padding: 14px 5px 11px; border: 1px solid #383838; border-left: 0; border-radius: 0 22px 22px 0; background: #252525e8; color: #eee; box-shadow: 2px 8px 24px #0008; backdrop-filter: blur(12px); cursor: pointer; transition: width .16s, background .16s; }.workspace-launcher:hover { width: 40px; background: #363636; }.workspace-launcher > span { writing-mode: vertical-rl; font-size: 12px; letter-spacing: .08em; }.workspace-launcher .launcher-arrow { font-size: 12px; opacity: .6; }
.workspace-backdrop { position: fixed; inset: 0; z-index: 4001; background: #000b; }
.workspace-drawer-enter-active, .workspace-drawer-leave-active { transition: transform .2s ease, opacity .2s ease; }.workspace-drawer-enter-from, .workspace-drawer-leave-to { transform: translateX(-100%); opacity: 0; }.workspace-shade-enter-active, .workspace-shade-leave-active { transition: opacity .18s; }.workspace-shade-enter-from, .workspace-shade-leave-to { opacity: 0; }
.workspace-navigation button:focus-visible, .workspace-launcher:focus-visible { outline: 2px solid #b7caae; outline-offset: 3px; }
@media (max-height: 790px) { .workspace-navigation { padding-top: 18px; padding-bottom: 14px; }.workspace-brand { padding-bottom: 18px; }.workspace-primary, .workspace-tools { gap: 3px; }.workspace-nav-item { min-height: 46px; }.workspace-tools .workspace-nav-item { min-height: 40px; }.workspace-project-context { margin-top: 12px; padding-top: 12px; }.workspace-tools { margin-top: 12px; }.workspace-project-context + .workspace-tools { margin-top: 6px; }.workspace-footer { padding-top: 14px; }.workspace-footer > p { display: none; } }
@media (prefers-reduced-motion: reduce) { .workspace-drawer-enter-active, .workspace-drawer-leave-active, .workspace-shade-enter-active, .workspace-shade-leave-active, .workspace-launcher, .workspace-nav-item { transition: none; } }
</style>
